/* eslint-disable max-len */
import {
  Email as EmailIcon,
  Home as LicenseIcon,
  Language as LanguageIcon,
  LocationOnOutlined as LocationOutlinedIcon,
  Phone as PhoneIcon,
  AccessTime as ClockIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import HauserLogo from '@/assets/images/hauser-logo.svg?react';
import { ShareStrip, StatRow, StatTile } from '@/components/admin/StatTile';
import { Switch } from '@/components/common/Switch';
import { UserAvatar } from '@/components/common/UserAvatar';
import { Button, IconButton, Segmented, SortCaret, TextInput } from '@/components/ds';
import { LANGUAGES, POPULAR_MARKETS } from '@/constants/profileOptions';
import { useSnackbar } from '@/context/SnackbarProvider';
import {
  useAdminUsers,
  useAdminUsersSearch,
  useChangeUserRole,
  useSetUserActivation,
  useDeleteUser,
  useUpdateAdminUserProfile,
  useToggleAdminAccess,
} from '@/hooks/admin/useAdminUsers';
import { useUserRole } from '@/hooks/useUserRole';
import { AdminUser, UpdateAdminUserProfileRequest } from '@/services/api/admin';
import { createInvite } from '@/services/api/invites';
import { useTranslationPrefix } from '@/translations/i18n';
import { formatPhone } from '@/utils/formatPhone';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg: 'var(--tx-fill-2)',
  card: 'var(--tx-glass-panel)',
  input: '#333333',
  border: 'var(--tx-line)',
  borderLight: 'var(--tx-line)',
  text: 'var(--tx-text)',
  muted: 'var(--tx-muted)',
  subtle: 'var(--tx-faint)',
  lime: '#c0ff02',
  danger: '#ff4d4d',
};
const L = {
  critical: '#DB5282',
  urgent: '#FF9A4D',
  moderate: '#FFF981',
  low: '#5AE9FF',
  monitor: '#9A938A',
  resolved: '#C0FF02',
};
const font = "'Manrope', sans-serif";

// ─── Role color mapping (lime reserved for status only) ──────────────────────
// Keyed by the label a row shows, so Buyer and Owner (both the Buyer role) read apart
const ROLE_COLORS: Record<string, string> = {
  SuperAdmin: L.critical,
  Admin: L.critical,
  Advisor: L.urgent,
  Buyer: L.low,
  Owner: L.moderate,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(date: string | undefined): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function relativeTime(date: string | undefined): string {
  if (!date) return '—';
  const ms = Date.now() - new Date(date).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

type BuyerCategory = 'buyer' | 'owner' | null;

// Buyer-role label: 'buyer' (invited, hasn't closed on a property yet) ->
// Buyer, 'owner' (self-serve with no invite history, or an invited buyer
// who has since closed) -> Owner.
const buyerRoleLabel = (buyerCategory: BuyerCategory): string =>
  buyerCategory === 'owner' ? 'Owner' : 'Buyer';

// The role as the app's urgency legend item: a 2px tick in the role's colour, then its name
function RoleBadge({ role, buyerCategory }: { role: string; buyerCategory?: BuyerCategory }) {
  const label = role === 'Buyer' ? buyerRoleLabel(buyerCategory ?? null) : role;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontWeight: 500,
        color: C.text,
        fontFamily: font,
        whiteSpace: 'nowrap',
      }}>
      <span style={{ width: 2, height: 12, borderRadius: 1, background: ROLE_COLORS[label] ?? C.subtle }} />
      {label === 'SuperAdmin' ? 'Super admin' : label}
    </span>
  );
}

// Status as one short bar: lime once active, moderate while it waits on the user's first sign-in, faint when off
function StatusDot({ enabled, userStatus }: { enabled: boolean; userStatus?: string }) {
  const confirmed = (userStatus ?? '').toUpperCase() === 'CONFIRMED';
  const color = !enabled ? C.subtle : confirmed ? L.resolved : L.moderate;
  const label = !enabled ? 'Disabled' : confirmed ? 'Active' : 'Awaiting first sign-in';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span aria-hidden style={{ width: 14, height: 3, borderRadius: 2, background: color }} />
      <span style={{ fontSize: 13, color: enabled ? C.text : C.muted, fontFamily: font, fontWeight: 500 }}>
        {label}
      </span>
    </div>
  );
}

// ─── Role dropdown (portal-based) ────────────────────────────────────────────
function RoleSelect({
  value,
  onChange,
  isPending,
  buyerCategory,
}: {
  value: string;
  onChange: (v: string) => void;
  isPending?: boolean;
  /** Shows Buyer/Owner for the current value based on the same status the
   *  row's RoleBadge uses. Doesn't affect the assignable role options
   *  themselves, since this status isn't a role you pick. */
  buyerCategory?: BuyerCategory;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, minWidth: 0 });
  const options = ['Admin', 'Advisor', 'Buyer'];

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div
      ref={ref}
      style={{ position: 'relative', display: 'inline-block' }}
      onClick={(e) => e.stopPropagation()}>
      <span
        onClick={(e) => {
          e.stopPropagation();
          if (isPending) return;
          if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            setPos({ top: rect.bottom + 6, left: rect.left, minWidth: Math.max(rect.width, 130) });
          }
          setOpen((o) => !o);
        }}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onMouseEnter={(e) => {
          if (!open) e.currentTarget.style.background = 'var(--tx-fill-2)';
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.background = 'var(--tx-fill)';
        }}
        style={{
          // reads as a picker: a tonal control with the role's tick, its name and a chevron
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          height: 26,
          boxSizing: 'border-box',
          padding: '0 8px 0 10px',
          background: open ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
          borderRadius: 7,
          cursor: isPending ? 'wait' : 'pointer',
          opacity: isPending ? 0.6 : 1,
          transition: 'background 0.15s',
        }}>
        <RoleBadge role={value} buyerCategory={buyerCategory} />
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke={C.muted}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </span>
      {open &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              zIndex: 9999,
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              borderRadius: '12px',
              padding: 4,
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              minWidth: pos.minWidth,
              boxShadow: 'var(--tx-shadow)',
            }}>
            {options.map((opt) => {
              const isSelected = opt === value;
              return (
                <div
                  key={opt}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(opt);
                    setOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    height: 32,
                    padding: '0 10px',
                    fontSize: 12,
                    fontWeight: 500,
                    fontFamily: font,
                    cursor: 'pointer',
                    borderRadius: 8,
                    color: C.text,
                    background: isSelected ? 'var(--tx-sel-bg)' : 'transparent',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLDivElement).style.background =
                        'var(--tx-fill)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                  }}>
                  <span style={{ width: 2, height: 12, borderRadius: 1, background: ROLE_COLORS[opt] }} />
                  {opt}
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}

// ─── Expanded row detail ─────────────────────────────────────────────────────
function ExpandedRow({
  user,
  onRoleChange,
  isRolePending,
  canManageAdminAccess,
  onAdminAccessToggle,
  isAdminAccessPending,
}: {
  user: AdminUser;
  onRoleChange: (role: string) => void;
  isRolePending: boolean;
  canManageAdminAccess: boolean;
  onAdminAccessToggle: (grant: boolean) => void;
  isAdminAccessPending: boolean;
}) {
  const canShowAdminAccess =
    canManageAdminAccess &&
    user.groups.some((group) => ['Advisor', 'Admin', 'SuperAdmin'].includes(group));

  const isAdminOn = user.groups.includes('Admin');
  const [showIds, setShowIds] = useState(false);

  // one fact: its name, then its value beside it
  const fact = (label: string, value: React.ReactNode, title?: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, minHeight: 34 }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: C.muted, fontFamily: font, width: 96, flexShrink: 0 }}>
        {label}
      </span>
      {typeof value === 'string' ? (
        <span
          title={title}
          style={{
            minWidth: 0,
            fontSize: 13,
            fontWeight: 500,
            color: C.text,
            fontFamily: font,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  );

  const group = (title: string, children: React.ReactNode) => (
    <>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.subtle, fontFamily: font, marginBottom: 8 }}>{title}</div>
      {children}
    </>
  );

  return (
    <div
      style={{
        // sits in the open row's box (see the row wrapper), 8px in from the table like the row above it
        padding: '10px 12px 20px',
      }}>
      {/* the table's own columns: user details under Name, account under Email */}
      <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 20 }}>
        <div style={{ gridColumn: '2', minWidth: 0 }}>
          {group(
            'User details',
            <>
              {fact('Created', fmtDate(user.createdAt))}
              {/* the raw identifiers, kept for reference but folded away until asked for */}
              <button
                type="button"
                onClick={() => setShowIds((v) => !v)}
                aria-expanded={showIds}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 34,
                  padding: 0,
                  border: 0,
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 500,
                  color: C.muted,
                  fontFamily: font,
                }}>
                IDs
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  style={{ transform: showIds ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
              {showIds && (
                <>
                  {user.username !== user.userId && fact('Username', user.username, user.username)}
                  {fact('User ID', user.userId, user.userId)}
                </>
              )}
            </>,
          )}
        </div>
        <div style={{ gridColumn: '3 / -1', minWidth: 0 }}>
          {group(
            'Account',
            <>
              {fact(
                'Role',
                <RoleSelect
                  value={user.platformRole}
                  onChange={onRoleChange}
                  isPending={isRolePending}
                  buyerCategory={user.buyerCategory}
                />,
              )}
              {canShowAdminAccess &&
                fact(
                  'Admin access',
                  <Tooltip title="Grant or revoke Admin access without changing the user's primary role.">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Switch
                        checked={isAdminOn}
                        disabled={isAdminAccessPending}
                        onChange={() => onAdminAccessToggle(!isAdminOn)}
                        sx={{ width: 40, '& .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(18px)' } }}
                        inputProps={{ 'aria-label': isAdminOn ? 'Revoke admin access' : 'Grant admin access' }}
                      />
                      <span style={{ color: isAdminOn ? C.text : C.muted }}>{isAdminOn ? 'On' : 'Off'}</span>
                    </span>
                  </Tooltip>,
                )}
              {fact('Status', <StatusDot enabled={user.enabled} userStatus={user.userStatus} />)}
            </>,
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Row context menu (3-dot vertical) ──────────────────────────────────────
function RowMenu({
  user,
  onEdit,
  onRoleChange,
  onToggleActivation,
  onDelete,
}: {
  user: AdminUser;
  onEdit: () => void;
  onRoleChange: (role: string) => void;
  onToggleActivation: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, right: 0, openUp: false });
  const [roleSubOpen, setRoleSubOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setRoleSubOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const menuItem = (
    label: string,
    onClick: () => void,
    opts?: { disabled?: boolean; danger?: boolean },
  ) => (
    <div
      onMouseDown={(e) => {
        e.preventDefault();
        if (!opts?.disabled) {
          onClick();
          setOpen(false);
          setRoleSubOpen(false);
        }
      }}
      style={{
        padding: '7px 12px',
        fontSize: 12,
        fontWeight: 500,
        fontFamily: font,
        cursor: opts?.disabled ? 'not-allowed' : 'pointer',
        borderRadius: 6,
        color: opts?.disabled ? C.subtle : opts?.danger ? C.danger : C.text,
        opacity: opts?.disabled ? 0.4 : 1,
        background: 'transparent',
        transition: 'background 0.1s',
      }}
      onMouseEnter={(e) => {
        if (!opts?.disabled)
          (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.08)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'transparent';
      }}>
      {label}
    </div>
  );

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const estimatedMenuHeight = 200;
            const openUp = window.innerHeight - rect.bottom < estimatedMenuHeight + 8;
            setPos({
              top: openUp ? rect.top - 4 : rect.bottom + 4,
              right: window.innerWidth - rect.right,
              openUp,
            });
          }
          setOpen((o) => !o);
          setRoleSubOpen(false);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          borderRadius: 6,
          background: open ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
          border: 0,
          color: C.subtle,
          cursor: 'pointer',
        }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: pos.top,
              right: pos.right,
              transform: pos.openUp ? 'translateY(-100%)' : 'none',
              zIndex: 9999,
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              borderRadius: '12px',
              padding: '4px 6px',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              minWidth: 160,
              boxShadow: 'var(--tx-shadow)',
            }}>
            {menuItem('Edit Details', onEdit)}
            <div
              onMouseEnter={() => setRoleSubOpen(true)}
              onMouseLeave={() => setRoleSubOpen(false)}
              style={{ position: 'relative' }}>
              <div
                style={{
                  padding: '7px 12px',
                  fontSize: 11,
                  fontWeight: 500,
                  fontFamily: font,
                  cursor: 'pointer',
                  borderRadius: 6,
                  color: C.text,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: roleSubOpen ? 'var(--tx-fill-2)' : 'transparent',
                  transition: 'background 0.1s',
                }}>
                Change Role
                <svg
                  width="8"
                  height="8"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>
              {roleSubOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: -4,
                    left: '100%',
                    marginLeft: 4,
                    background: 'var(--tx-glass)',
                    borderRadius: '12px',
                    padding: '4px 6px',
                    backdropFilter: 'var(--tx-blur)',
                    WebkitBackdropFilter: 'var(--tx-blur)',
                    minWidth: 100,
                    boxShadow: 'var(--tx-shadow-sm)',
                  }}>
                  {['Admin', 'Advisor', 'Buyer'].map((role) => {
                    const isCurrent = user.platformRole === role;
                    return (
                      <div
                        key={role}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (!isCurrent) {
                            onRoleChange(role);
                            setOpen(false);
                            setRoleSubOpen(false);
                          }
                        }}
                        style={{
                          padding: '6px 10px',
                          fontSize: 11,
                          fontWeight: isCurrent ? 600 : 500,
                          fontFamily: font,
                          cursor: isCurrent ? 'default' : 'pointer',
                          borderRadius: 6,
                          color: isCurrent ? C.muted : C.text,
                          background: 'transparent',
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={(e) => {
                          if (!isCurrent)
                            (e.currentTarget as HTMLDivElement).style.background =
                              'rgba(255,255,255,0.08)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                        }}>
                        {role}
                        {isCurrent ? ' ✓' : ''}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '4px 6px' }} />
            {menuItem(user.enabled ? 'Deactivate' : 'Activate', onToggleActivation)}
            {menuItem('Delete User', onDelete, { danger: true })}
          </div>,
          document.body,
        )}
    </div>
  );
}

// ─── ID card icon (clickable, advisors only) ────────────────────────────────
function IdCardIcon({ onClick }: { onClick: (e: React.MouseEvent) => void }) {
  return (
    <button
      onClick={onClick}
      title="View profile card"
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: C.subtle,
        transition: 'color 0.15s',
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.color = C.muted;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.color = C.subtle;
      }}>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <circle cx="8" cy="11" r="2.5" />
        <path d="M4 18c0-2 1.8-3 4-3s4 1 4 3" />
        <line x1="15" y1="9" x2="20" y2="9" />
        <line x1="15" y1="13" x2="20" y2="13" />
      </svg>
    </button>
  );
}

// ─── Advisor profile card overlay ───────────────────────────────────────────
function AdvisorProfileCard({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const fullName = user.name || user.email || user.username;

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'var(--tx-backdrop)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 300, position: 'relative' }}>
        {/* Card — portrait orientation */}
        <div
          style={{
            borderRadius: '24px',
            backgroundColor: 'var(--tx-glass-panel)',
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            overflow: 'hidden',
            position: 'relative',
            height: 500,
            display: 'flex',
            flexDirection: 'column',
          }}>
          {/* Avatar + name hero */}
          <div
            style={{
              paddingTop: 60,
              paddingBottom: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '60px 25px 10px',
            }}>
            <Box sx={{ mb: '20px' }}>
              <UserAvatar
                name={fullName}
                photoUrl={user.profile?.photoUrl}
                size={78}
                showPresence={false}
              />
            </Box>
            <div
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: 'var(--tx-text)',
                fontFamily: font,
                lineHeight: 1.3,
              }}>
              {fullName}
            </div>
            <div style={{ fontSize: 12, color: 'var(--tx-faint)', fontFamily: font, marginTop: 4 }}>
              {user.platformRole === 'Admin' ? 'Admin' : 'Real Estate Advisor'}
            </div>
          </div>

          {/* Divider */}
          <div style={{ margin: '20px 25px', borderTop: '1px solid rgba(255,255,255,0.05)' }} />

          {/* Contact details */}
          <div style={{ padding: '0 25px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Email */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <EmailIcon
                sx={{
                  fontSize: 15,
                  color: user.email ? 'var(--tx-faint)' : '#8b868040',
                  transition: 'color 0.3s',
                }}
              />
              <span
                style={{
                  fontSize: 12,
                  color: user.email ? 'var(--tx-muted)' : '#8b868040',
                  fontFamily: font,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                {user.email || '—'}
              </span>
            </div>
            {/* Phone */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <PhoneIcon
                sx={{ fontSize: 15, color: user.profile?.phoneNumber ? 'var(--tx-faint)' : '#8b868040' }}
              />
              <span
                style={{
                  fontSize: 12,
                  color: user.profile?.phoneNumber ? 'var(--tx-muted)' : '#8b868040',
                  fontFamily: font,
                }}>
                {user.profile?.phoneNumber
                  ? formatPhone(user.profile.phoneNumber)
                  : '(###) ###-####'}
              </span>
            </div>
            {/* DRE License */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <LicenseIcon
                sx={{ fontSize: 15, color: user.profile?.dreNumber ? 'var(--tx-faint)' : '#8b868040' }}
              />
              <span
                style={{
                  fontSize: 12,
                  color: user.profile?.dreNumber ? 'var(--tx-muted)' : '#8b868040',
                  fontFamily: font,
                }}>
                {user.profile?.dreNumber ? `DRE #${user.profile.dreNumber}` : 'DRE license'}
              </span>
            </div>
            {/* Status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <ClockIcon sx={{ fontSize: 15, color: 'var(--tx-faint)' }} />
              <span style={{ fontSize: 12, color: 'var(--tx-muted)', fontFamily: font }}>
                {user.enabled ? 'Active' : 'Disabled'} · Joined{' '}
                {user.createdAt
                  ? new Date(user.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      year: 'numeric',
                    })
                  : '—'}
              </span>
            </div>
          </div>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Hauser logo + icon row footer — same as onboarding card */}
          <div
            style={{
              padding: '0 25px 25px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
            }}>
            <HauserLogo style={{ width: 125, height: 'auto', color: '#bbb7af' }} />
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 15 }}>
              <Tooltip
                title={
                  user.profile?.markets?.length ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: 4 }}>
                      {user.profile.markets.map((m) => (
                        <span
                          key={m}
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: 'var(--tx-text)',
                            background: 'var(--tx-fill-2)',
                            borderRadius: 6,
                            padding: '2px 8px',
                            whiteSpace: 'nowrap',
                          }}>
                          {m}
                        </span>
                      ))}
                    </div>
                  ) : (
                    'No markets'
                  )
                }
                placement="left"
                arrow
                PopperProps={{ style: { zIndex: 10001 }, disablePortal: true }}>
                <div style={{ display: 'flex', cursor: 'pointer' }}>
                  <LocationOutlinedIcon
                    sx={{
                      fontSize: 28,
                      color: user.profile?.markets?.length ? 'var(--tx-faint)' : 'var(--tx-text)',
                      opacity: user.profile?.markets?.length ? 1 : 0.3,
                      transition: 'color 0.15s',
                      '&:hover': user.profile?.markets?.length ? { color: 'var(--tx-muted)' } : undefined,
                    }}
                  />
                </div>
              </Tooltip>
              <Tooltip
                title={
                  user.profile?.languages?.length ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: 4 }}>
                      {user.profile.languages.map((l) => (
                        <span
                          key={l}
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: 'var(--tx-muted)',
                            background: 'var(--tx-fill-2)',
                            borderRadius: 6,
                            padding: '2px 8px',
                            whiteSpace: 'nowrap',
                          }}>
                          {l}
                        </span>
                      ))}
                    </div>
                  ) : (
                    'No languages'
                  )
                }
                placement="left"
                arrow
                PopperProps={{ style: { zIndex: 10001 }, disablePortal: true }}>
                <div style={{ display: 'flex', cursor: 'pointer' }}>
                  <LanguageIcon
                    sx={{
                      fontSize: 28,
                      color: user.profile?.languages?.length ? 'var(--tx-faint)' : 'var(--tx-text)',
                      opacity: user.profile?.languages?.length ? 1 : 0.3,
                      transition: 'color 0.15s',
                      '&:hover': user.profile?.languages?.length ? { color: 'var(--tx-muted)' } : undefined,
                    }}
                  />
                </div>
              </Tooltip>
              {(() => {
                // linkedinUrl is stored as a bare slug (e.g. "johndoe") after SettingsView
                // strips the linkedin.com/in/ prefix. Normalize to a full https URL and
                // allowlist only http/https schemes to prevent javascript: injection.
                const raw = user.profile?.linkedinUrl?.trim();
                if (!raw) {
                  return (
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="#333333"
                      style={{ opacity: 0.3 }}>
                      <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                    </svg>
                  );
                }
                // If not already an absolute https URL, treat as a slug
                const href = /^https?:\/\//i.test(raw)
                  ? raw
                  : `https://www.linkedin.com/in/${raw.replace(/^\/+/, '')}`;
                // Final allowlist — only render a link for http/https
                if (!/^https?:\/\//i.test(href)) {
                  return (
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="#333333"
                      style={{ opacity: 0.3 }}>
                      <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                    </svg>
                  );
                }
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'flex', color: 'inherit' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="#8b8680">
                      <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                    </svg>
                  </a>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'var(--tx-fill-2)',
            border: 'none',
            color: 'var(--tx-faint)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>,
    document.body,
  );
}

// ─── Edit Details dialog ─────────────────────────────────────────────────────

const normalizeLinkedin = (v: string) =>
  v
    .trim()
    .replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//i, '')
    .replace(/^(www\.)?linkedin\.com\/in\//i, '')
    .replace(/\/+$/g, '');

function EditDetailsDialog({
  user,
  onClose,
  onSave,
  isSaving,
}: {
  user: AdminUser;
  onClose: () => void;
  onSave: (fields: UpdateAdminUserProfileRequest) => void;
  isSaving: boolean;
}) {
  const isAdvisor = user.platformRole === 'Advisor';
  const isBuyer = user.platformRole === 'Buyer' || user.platformRole === 'Admin';
  const p = user.profile;

  const [firstName, setFirstName] = useState(p?.firstName ?? '');
  const [lastName, setLastName] = useState(p?.lastName ?? '');
  const [phone, setPhone] = useState(p?.phoneNumber ? String(p.phoneNumber) : '');
  const [addressLine1, setAddressLine1] = useState(p?.addressLine1 ?? '');
  const [addressLine2, setAddressLine2] = useState(p?.addressLine2 ?? '');
  const [city, setCity] = useState(p?.city ?? '');
  const [state, setState] = useState(p?.state ?? '');
  const [postalCode, setPostalCode] = useState(p?.postalCode ? String(p.postalCode) : '');
  const [dreNumber, setDreNumber] = useState(p?.dreNumber ?? '');
  const [dreExpiry, setDreExpiry] = useState(p?.dreExpiry ?? '');
  const [linkedinUrl, setLinkedinUrl] = useState(normalizeLinkedin(p?.linkedinUrl ?? ''));
  const [bio, setBio] = useState(p?.bio ?? '');
  const [yearsExp, setYearsExp] = useState(p?.yearsExperience ?? '');
  const [markets, setMarkets] = useState<string[]>(p?.markets ?? []);
  const [languages, setLanguages] = useState<string[]>(p?.languages ?? []);

  // Re-sync when the live user object updates (background refetch or different user)
  useEffect(() => {
    const prof = user.profile;
    setFirstName(prof?.firstName ?? '');
    setLastName(prof?.lastName ?? '');
    setPhone(prof?.phoneNumber ? String(prof.phoneNumber) : '');
    setAddressLine1(prof?.addressLine1 ?? '');
    setAddressLine2(prof?.addressLine2 ?? '');
    setCity(prof?.city ?? '');
    setState(prof?.state ?? '');
    setPostalCode(prof?.postalCode ? String(prof.postalCode) : '');
    setDreNumber(prof?.dreNumber ?? '');
    setDreExpiry(prof?.dreExpiry ?? '');
    setLinkedinUrl(normalizeLinkedin(prof?.linkedinUrl ?? ''));
    setBio(prof?.bio ?? '');
    setYearsExp(prof?.yearsExperience ?? '');
    setMarkets(prof?.markets ?? []);
    setLanguages(prof?.languages ?? []);
  }, [user.userId, user.profile]); // re-sync when background refetch updates profile for same user

  const toggleMarket = (m: string) =>
    setMarkets((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  const toggleLanguage = (l: string) =>
    setLanguages((prev) => (prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]));

  const inputStyle: React.CSSProperties = {
    background: 'var(--tx-fill)',
    border: 0,
    borderRadius: 8,
    padding: '9px 12px',
    color: C.text,
    fontSize: 13,
    fontFamily: font,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: C.muted,
    fontFamily: font,
    marginBottom: 6,
    display: 'block',
  };

  const chipStyle = (active: boolean): React.CSSProperties => ({
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    fontFamily: font,
    cursor: 'pointer',
    border: 0,
    background: active ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
    color: active ? C.muted : C.subtle,
    transition: 'all 0.15s',
  });

  const handleSave = () => {
    const phoneNum = phone.replace(/\D/g, '');
    const pc = postalCode.replace(/\D/g, '');

    // Only include name fields when they actually changed so the Lambda
    // doesn't trigger an unnecessary Cognito `name` sync on every save.
    const initialFirst = p?.firstName ?? '';
    const initialLast = p?.lastName ?? '';
    const nameFields: Pick<UpdateAdminUserProfileRequest, 'firstName' | 'lastName'> = {};
    if (firstName.trim() !== initialFirst) nameFields.firstName = firstName.trim();
    if (lastName.trim() !== initialLast) nameFields.lastName = lastName.trim();

    const base: UpdateAdminUserProfileRequest = {
      ...nameFields,
      phoneNumber: phoneNum ? Number(phoneNum) : null,
    };

    if (isAdvisor) {
      onSave({
        ...base,
        dreNumber: dreNumber.trim(),
        dreExpiry: dreExpiry.trim(),
        linkedinUrl: linkedinUrl.trim(),
        bio: bio.trim(),
        yearsExperience: yearsExp.trim(),
        markets,
        languages,
      });
    } else {
      // Buyer + Admin: phone + address (+ name if changed)
      onSave({
        ...base,
        addressLine1: addressLine1.trim(),
        addressLine2: addressLine2.trim(),
        city: city.trim(),
        state: state.trim(),
        postalCode: pc ? Number(pc) : null,
      });
    }
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'var(--tx-backdrop)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-details-dialog-title"
        tabIndex={-1}
        style={{
          background: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRadius: '24px',
          padding: 28,
          width: 520,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          boxShadow: 'var(--tx-shadow)',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div
              id="edit-details-dialog-title"
              style={{ fontSize: 15, fontWeight: 600, color: C.text, fontFamily: font }}>
              Edit details
            </div>
            <div style={{ fontSize: 12, color: C.subtle, fontFamily: font, marginTop: 2 }}>
              {user.name || user.email}
            </div>
          </div>
          <button
            aria-label="Close"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: C.subtle,
              cursor: 'pointer',
              padding: 4,
            }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Name + Phone — all roles */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label htmlFor="edit-first-name" style={labelStyle}>
              First Name
            </label>
            <input
              id="edit-first-name"
              style={inputStyle}
              type="text"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onFocus={(e) => {
                e.target.style.borderColor = C.muted;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = C.border;
              }}
            />
          </div>
          <div>
            <label htmlFor="edit-last-name" style={labelStyle}>
              Last Name
            </label>
            <input
              id="edit-last-name"
              style={inputStyle}
              type="text"
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onFocus={(e) => {
                e.target.style.borderColor = C.muted;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = C.border;
              }}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="edit-phone" style={labelStyle}>
              Phone
            </label>
            <input
              id="edit-phone"
              style={inputStyle}
              type="tel"
              placeholder="(555) 555-5555"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onFocus={(e) => {
                e.target.style.borderColor = C.muted;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = C.border;
              }}
            />
          </div>
        </div>

        {/* Buyer-specific: address */}
        {isBuyer && (
          <>
            <div>
              <label htmlFor="edit-address-line1" style={labelStyle}>
                Address
              </label>
              <input
                id="edit-address-line1"
                style={{ ...inputStyle, marginBottom: 8 }}
                type="text"
                placeholder="Street address"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                onFocus={(e) => {
                  e.target.style.borderColor = C.muted;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = C.border;
                }}
              />
              <input
                style={inputStyle}
                type="text"
                placeholder="Apt, suite, etc. (optional)"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
                onFocus={(e) => {
                  e.target.style.borderColor = C.muted;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = C.border;
                }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px', gap: 10 }}>
              <div>
                <label htmlFor="edit-city" style={labelStyle}>
                  City
                </label>
                <input
                  id="edit-city"
                  style={inputStyle}
                  type="text"
                  placeholder="City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  onFocus={(e) => {
                    e.target.style.borderColor = C.muted;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = C.border;
                  }}
                />
              </div>
              <div>
                <label htmlFor="edit-state" style={labelStyle}>
                  State
                </label>
                <input
                  id="edit-state"
                  style={inputStyle}
                  type="text"
                  placeholder="CA"
                  maxLength={2}
                  value={state}
                  onChange={(e) => setState(e.target.value.toUpperCase())}
                  onFocus={(e) => {
                    e.target.style.borderColor = C.muted;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = C.border;
                  }}
                />
              </div>
              <div>
                <label htmlFor="edit-zip" style={labelStyle}>
                  ZIP
                </label>
                <input
                  id="edit-zip"
                  style={inputStyle}
                  type="text"
                  placeholder="90210"
                  maxLength={5}
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, ''))}
                  onFocus={(e) => {
                    e.target.style.borderColor = C.muted;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = C.border;
                  }}
                />
              </div>
            </div>
          </>
        )}

        {/* Advisor-specific: professional info */}
        {isAdvisor && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label htmlFor="edit-dre-number" style={labelStyle}>
                  DRE Number
                </label>
                <input
                  id="edit-dre-number"
                  style={inputStyle}
                  type="text"
                  placeholder="01234567"
                  value={dreNumber}
                  onChange={(e) => setDreNumber(e.target.value)}
                  onFocus={(e) => {
                    e.target.style.borderColor = C.muted;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = C.border;
                  }}
                />
              </div>
              <div>
                <label htmlFor="edit-dre-expiry" style={labelStyle}>
                  DRE Expiry
                </label>
                <input
                  id="edit-dre-expiry"
                  style={inputStyle}
                  type="date"
                  value={dreExpiry}
                  onChange={(e) => setDreExpiry(e.target.value)}
                  onFocus={(e) => {
                    e.target.style.borderColor = C.muted;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = C.border;
                  }}
                />
              </div>
              <div>
                <label htmlFor="edit-years-exp" style={labelStyle}>
                  Years Experience
                </label>
                <input
                  id="edit-years-exp"
                  style={inputStyle}
                  type="text"
                  placeholder="e.g. 5–10 years"
                  value={yearsExp}
                  onChange={(e) => setYearsExp(e.target.value)}
                  onFocus={(e) => {
                    e.target.style.borderColor = C.muted;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = C.border;
                  }}
                />
              </div>
              <div>
                <label htmlFor="edit-linkedin" style={labelStyle}>
                  LinkedIn Username
                </label>
                <input
                  id="edit-linkedin"
                  style={inputStyle}
                  type="text"
                  placeholder="username"
                  value={linkedinUrl}
                  onChange={(e) => setLinkedinUrl(normalizeLinkedin(e.target.value))}
                  onFocus={(e) => {
                    e.target.style.borderColor = C.muted;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = C.border;
                  }}
                />
              </div>
            </div>

            <div>
              <label htmlFor="edit-bio" style={labelStyle}>
                Bio
              </label>
              <textarea
                id="edit-bio"
                style={
                  {
                    ...inputStyle,
                    resize: 'vertical',
                    minHeight: 72,
                    lineHeight: 1.5,
                  } as React.CSSProperties
                }
                placeholder="Professional bio..."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                onFocus={(e) => {
                  e.target.style.borderColor = C.muted;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = C.border;
                }}
              />
            </div>

            <div>
              <label style={labelStyle}>Markets</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {POPULAR_MARKETS.map((m) => (
                  <button
                    key={m}
                    aria-pressed={markets.includes(m)}
                    onClick={() => toggleMarket(m)}
                    style={chipStyle(markets.includes(m))}>
                    {m}
                  </button>
                ))}
                {markets
                  .filter((m) => !POPULAR_MARKETS.includes(m))
                  .map((m) => (
                    <button
                      key={m}
                      aria-pressed
                      onClick={() => toggleMarket(m)}
                      style={chipStyle(true)}>
                      {m} ×
                    </button>
                  ))}
              </div>
            </div>

            <div>
              <label style={labelStyle}>Languages</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {LANGUAGES.map((l) => (
                  <button
                    key={l}
                    aria-pressed={languages.includes(l)}
                    onClick={() => toggleLanguage(l)}
                    style={chipStyle(languages.includes(l))}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              background: 'var(--tx-fill)',
              border: 0,
              borderRadius: 8,
              padding: '9px 20px',
              color: C.muted,
              fontSize: 12,
              fontWeight: 600,
              fontFamily: font,
              cursor: 'pointer',
            }}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            style={{
              background: isSaving ? C.input : C.muted,
              border: 'none',
              borderRadius: 8,
              padding: '9px 20px',
              color: C.card,
              fontSize: 12,
              fontWeight: 600,
              fontFamily: font,
              cursor: isSaving ? 'wait' : 'pointer',
              opacity: isSaving ? 0.6 : 1,
            }}>
            {isSaving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Grid column template ────────────────────────────────────────────────────
const GRID = '28px minmax(0, 1.5fr) minmax(0, 1.5fr) 90px 80px 100px 48px';

// ─── Sort ────────────────────────────────────────────────────────────────────
type SortKey = 'name' | 'email' | 'role' | 'status' | 'created';

// ─── Main component ─────────────────────────────────────────────────────────
export const UsersTab = () => {
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const { isSuperAdmin, isAdmin } = useUserRole();
  // Full auto-fetch — kept only for the role-count stat tiles and the
  // edit-user lookup below, both of which genuinely need the whole user
  // base (same rationale as PropertiesPageNew's KpiBar). The actual list
  // rows use useAdminUsersSearch instead — real server-side pagination +
  // search, not a client-side filter over this full array (see #1268).
  const { users, isLoading, error } = useAdminUsers();
  const changeRoleMutation = useChangeUserRole();
  const toggleAdminAccessMutation = useToggleAdminAccess();
  const setUserActivationMutation = useSetUserActivation();
  const deleteUserMutation = useDeleteUser();
  const updateProfileMutation = useUpdateAdminUserProfile();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  const {
    users: searchedUsers,
    isLoading: isLoadingSearchedUsers,
    hasNextPage: hasNextUsersPage,
    isFetchingNextPage: isFetchingNextUsersPage,
    fetchNextPage: fetchNextUsersPage,
  } = useAdminUsersSearch({ search: debouncedSearchQuery });
  // True while a search result is pending — either the debounce hasn't
  // settled yet or the debounced request is still in flight. Without this,
  // the empty state below would flash "No users match your filters" for
  // every keystroke/new page before the real (possibly non-empty) result
  // lands, since searchedUsers is briefly [] during that window.
  const isSearchPending = searchQuery.trim() !== debouncedSearchQuery || isLoadingSearchedUsers;
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [profileCardUser, setProfileCardUser] = useState<AdminUser | null>(null);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [deactivateConfirmUser, setDeactivateConfirmUser] = useState<AdminUser | null>(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<AdminUser | null>(null);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [inviteForm, setInviteForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'Advisor' as 'Advisor' | 'Admin',
  });
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  // Filter. Base list (searchedUsers) already ran the search server-side
  // (see useAdminUsersSearch) — the searchQuery re-check here is a harmless,
  // instant client-side refinement that keeps the list feeling responsive
  // during the 300ms debounce window before the server call lands.
  // roleFilter has no Cognito equivalent (it's derived — platform role and
  // buyerCategory come from per-user Cognito-group + DynamoDB enrichment,
  // not a raw Cognito attribute) so it stays a client-side filter over
  // whatever page(s) are currently loaded.
  const filtered = useMemo(() => {
    let result = searchedUsers;

    if (roleFilter !== 'all') {
      result =
        roleFilter === 'buyer' || roleFilter === 'owner'
          ? result.filter((u) => u.buyerCategory === roleFilter)
          : roleFilter === 'Admin'
            ? result.filter((u) => u.platformRole === 'Admin' || u.platformRole === 'SuperAdmin')
            : result.filter((u) => u.platformRole === roleFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (u) =>
          u.name?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          u.username.toLowerCase().includes(q),
      );
    }

    return result;
  }, [searchedUsers, searchQuery, roleFilter]);

  // Sort
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      if (sortKey === 'name') {
        av = (a.name || a.email || '').toLowerCase();
        bv = (b.name || b.email || '').toLowerCase();
      } else if (sortKey === 'email') {
        av = (a.email || '').toLowerCase();
        bv = (b.email || '').toLowerCase();
      } else if (sortKey === 'role') {
        av = a.platformRole;
        bv = b.platformRole;
      } else if (sortKey === 'status') {
        av = a.enabled ? 0 : 1;
        bv = b.enabled ? 0 : 1;
      } else if (sortKey === 'created') {
        av = a.createdAt || '';
        bv = b.createdAt || '';
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleRoleChange = (user: AdminUser, newRole: string) => {
    changeRoleMutation.mutate(
      {
        username: user.username,
        role: newRole as 'Admin' | 'Advisor' | 'Buyer',
      },
      {
        onSuccess: () => showAlert(t('user.role_changed'), 'success'),
        onError: () => showAlert(t('user.role_change_failed'), 'error'),
      },
    );
  };

  const handleAdminAccessToggle = (user: AdminUser, grant: boolean) => {
    toggleAdminAccessMutation.mutate(
      { username: user.username, grant },
      {
        onSuccess: () =>
          showAlert(t(grant ? 'user.admin_granted' : 'user.admin_revoked'), 'success'),
        onError: () => showAlert(t('user.admin_toggle_failed'), 'error'),
      },
    );
  };

  const handleConfirmDeactivation = () => {
    if (!deactivateConfirmUser) return;
    const willActivate = !deactivateConfirmUser.enabled;
    setUserActivationMutation.mutate(
      { username: deactivateConfirmUser.username, activate: willActivate },
      {
        onSuccess: () =>
          showAlert(t(willActivate ? 'user.activated' : 'user.deactivated'), 'success'),
        onError: () => showAlert(t('user.activation_failed'), 'error'),
      },
    );
    setDeactivateConfirmUser(null);
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirmUser) return;
    deleteUserMutation.mutate(
      { username: deleteConfirmUser.username },
      {
        onSuccess: () => showAlert(t('user.deleted'), 'success'),
        onError: () => showAlert(t('user.delete_failed'), 'error'),
      },
    );
    setDeleteConfirmUser(null);
    setDeleteConfirmInput('');
  };

  const handleStatusToggle = (user: AdminUser) => {
    if (user.enabled) {
      setDeactivateConfirmUser(user);
      return;
    }

    setUserActivationMutation.mutate(
      { username: user.username, activate: true },
      {
        onSuccess: () => showAlert(t('user.activated'), 'success'),
        onError: () => showAlert(t('user.activation_failed'), 'error'),
      },
    );
  };

  // Stats
  const adminCount = users.filter((u) => u.platformRole === 'Admin').length;
  const superCount = users.filter((u) => u.platformRole === 'SuperAdmin').length;
  const advisorCount = users.filter((u) => u.platformRole === 'Advisor').length;
  const buyerCount = users.filter((u) => u.platformRole === 'Buyer').length;
  const ownerCount = users.filter((u) => u.platformRole === 'Buyer' && u.buyerCategory === 'owner').length;
  const allExpanded = sorted.length > 0 && sorted.every((u) => expandedIds.has(u.userId));

  const headerCell = (label: string, key: SortKey) => (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--tx-muted)',
        fontFamily: font,
        cursor: 'pointer',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
      }}
      onClick={() => toggleSort(key)}>
      {label}
      <SortCaret active={sortKey === key} dir={sortDir} />
    </div>
  );

  if (error) {
    return (
      <div style={{ padding: 40 }}>
        <div style={{ background: C.card, borderRadius: 16, padding: 24, fontFamily: font }}>
          <span style={{ color: L.critical, fontSize: 14 }}>
            Error loading users: {error.message}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="admin-users-tab"
      style={{
        height: '100vh',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '20px 0',
        scrollbarWidth: 'none',
      }}>
      {/* ─── Stat cards: the app's tile, value first, label beneath, a share strip and a caption ─── */}
      <StatRow columns={4}>
        <StatTile
          loading={isLoading}
          value={users.length}
          label="Total users"
          visual={
            <ShareStrip
              parts={[
                { label: 'Buyers', color: ROLE_COLORS.Buyer, value: buyerCount - ownerCount },
                { label: 'Owners', color: ROLE_COLORS.Owner, value: ownerCount },
                { label: 'Advisors', color: ROLE_COLORS.Advisor, value: advisorCount },
                { label: 'Admins', color: ROLE_COLORS.Admin, value: adminCount + superCount },
              ]}
            />
          }
          caption={['All accounts', `${advisorCount + adminCount + superCount} staff`]}
        />
        <StatTile
          loading={isLoading}
          value={buyerCount}
          label="Clients"
          visual={
            <ShareStrip
              total={users.length}
              parts={[
                { label: 'Buyers', color: ROLE_COLORS.Buyer, value: buyerCount - ownerCount },
                { label: 'Owners', color: ROLE_COLORS.Owner, value: ownerCount },
              ]}
            />
          }
          caption={[`${buyerCount - ownerCount} buyers`, `${ownerCount} owners`]}
        />
        <StatTile
          loading={isLoading}
          value={advisorCount}
          label="Advisors"
          visual={
            <ShareStrip total={users.length} parts={[{ label: 'Advisors', color: ROLE_COLORS.Advisor, value: advisorCount }]} />
          }
          caption="Licensed agents"
        />
        <StatTile
          loading={isLoading}
          value={adminCount + superCount}
          label="Admins"
          visual={
            <ShareStrip
              total={users.length}
              parts={[{ label: 'Admins', color: ROLE_COLORS.Admin, value: adminCount + superCount }]}
            />
          }
          caption={[`${adminCount} admin`, `${superCount} super`]}
        />
      </StatRow>

      {/* ─── Toolbar ──────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <TextInput
          icon="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search name, email, or username…"
          sx={{ flex: '0 1 320px' }}
        />

        <Segmented
          label="Filter by role"
          value={roleFilter}
          onChange={setRoleFilter}
          options={(
            [
              ['all', 'All'],
              ['Admin', 'Admin'],
              ['Advisor', 'Advisor'],
              ['buyer', 'Buyer'],
              ['owner', 'Owner'],
            ] as const
          ).map(([key, label]) => [
            key,
            <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {key !== 'all' && (
                <span style={{ width: 2, height: 12, borderRadius: 1, background: ROLE_COLORS[label] }} />
              )}
              {label}
            </span>,
          ])}
        />

        <div style={{ flex: 1 }} />

        {/* Staff invitations are Admin/SuperAdmin-only. */}
        {isAdmin && (
          <Button
            variant="add"
            onClick={() => {
              setInviteDialogOpen(true);
              setInviteError(null);
              setInviteSuccess(false);
              setInviteForm({ email: '', firstName: '', lastName: '', role: 'Advisor' });
            }}>
            Invite user
          </Button>
        )}

        <IconButton
          icon={allExpanded ? 'shrink' : 'expand'}
          label={allExpanded ? 'Collapse all' : 'Expand all'}
          onClick={() =>
            setExpandedIds(allExpanded ? new Set() : new Set(sorted.map((u) => u.userId)))
          }
        />
      </div>

      {/* ─── Table ────────────────────────────────────────── */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            background: C.card,
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            borderRadius: '24px',
            overflow: 'hidden',
            paddingBottom: 5,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}>
          {/* Header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: GRID,
              gap: 20,
              padding: '19px 20px 14px',
              alignItems: 'center',
              borderBottom: `1px solid rgba(255,255,255,0.06)`,
            }}>
            <div />
            {headerCell('Name', 'name')}
            {headerCell('Email', 'email')}
            {headerCell('Role', 'role')}
            {headerCell('Status', 'status')}
            {headerCell('Joined', 'created')}
            <div />
          </div>

          {/* Rows */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', scrollbarWidth: 'none' }}>
            {isSearchPending ? (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <div style={{ fontSize: 13, color: C.subtle, fontFamily: font }}>Loading…</div>
              </div>
            ) : sorted.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <div style={{ fontSize: 13, color: C.subtle, fontFamily: font }}>
                  {searchQuery || roleFilter !== 'all'
                    ? 'No users match your filters'
                    : 'No users yet'}
                </div>
              </div>
            ) : (
              sorted.map((user, idx) => {
                const isExpanded = expandedIds.has(user.userId);
                const isLast = idx === sorted.length - 1;
                const displayName = user.name || user.email || user.username;

                return (
                  <div
                    key={user.userId}
                    style={
                      // an open row and its detail are one raised box, the app's open-row treatment (Advisor-Client's
                      // open client): inset 8px from the table, 16px corners, glass card, top light, small drop. Its
                      // contents pull in by the same 8px so they stay on the header's columns
                      isExpanded
                        ? {
                            position: 'relative',
                            zIndex: 1,
                            margin: '10px 8px',
                            borderRadius: 16,
                            overflow: 'hidden',
                            background: 'var(--tx-glass-card)',
                            backgroundImage: 'var(--tx-surface-light)',
                            boxShadow: 'var(--tx-shadow-sm)',
                          }
                        : undefined
                    }>
                    <div
                      onClick={(e) => {
                        // the hover tint goes as the row opens into its box
                        e.currentTarget.style.background = 'transparent';
                        setExpandedIds((prev) => {
                          const next = new Set(prev);
                          if (isExpanded) next.delete(user.userId);
                          else next.add(user.userId);
                          return next;
                        });
                      }}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: GRID,
                        gap: 20,
                        padding: isExpanded ? '12px 12px' : '12px 20px',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        background: 'transparent',
                        borderBottom:
                          isExpanded || isLast ? 'none' : `1px solid rgba(255,255,255,0.06)`,
                      }}
                      onMouseEnter={(e) => {
                        if (!isExpanded) e.currentTarget.style.background = 'var(--tx-fill)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}>
                      {/* Chevron */}
                      <div
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg
                          style={{
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                            color: C.subtle,
                          }}
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round">
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </div>

                      {/* Name + avatar + ID card icon for advisors */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <UserAvatar
                          name={displayName}
                          photoUrl={user.profile?.photoUrl}
                          size={28}
                          showPresence={false}
                        />
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: C.text,
                              fontFamily: font,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}>
                            {displayName}
                            {user.platformRole === 'Advisor' && (
                              <IdCardIcon
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setProfileCardUser(user);
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Email */}
                      <div
                        style={{
                          fontSize: 13,
                          color: C.muted,
                          fontFamily: font,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                        }}>
                        {user.email || '—'}
                      </div>

                      {/* Role */}
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <RoleBadge role={user.platformRole} buyerCategory={user.buyerCategory} />
                      </div>

                      {/* Status */}
                      <div
                        onClick={(event) => event.stopPropagation()}
                        style={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title={user.enabled ? 'Deactivate user' : 'Activate user'}>
                          <span>
                            <Switch
                              checked={user.enabled}
                              disabled={setUserActivationMutation.isPending}
                              onChange={() => handleStatusToggle(user)}
                              // Scoped to this table only — the shared Switch
                              // component (used on 3 other pages) keeps its
                              // default 44px width. Checked-state translateX
                              // is computed from this component's own known
                              // dimensions (16px thumb, 40px track) so the
                              // thumb lands 3px from the right edge — the
                              // same 3px gap its vertical padding already
                              // uses (top/bottom), not MUI's unrelated
                              // default distance tuned for its own sizing.
                              sx={{
                                width: 40,
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  transform: 'translateX(18px)',
                                },
                              }}
                              inputProps={{
                                'aria-label': user.enabled ? 'Deactivate user' : 'Activate user',
                              }}
                            />
                          </span>
                        </Tooltip>
                      </div>

                      {/* Joined */}
                      <div style={{ fontSize: 12, color: C.muted, fontFamily: font }}>
                        {relativeTime(user.createdAt)}
                      </div>

                      {/* Context menu */}
                      <div
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <RowMenu
                          user={user}
                          onEdit={() => setEditingUser(user)}
                          onRoleChange={(role) => handleRoleChange(user, role)}
                          onToggleActivation={() => setDeactivateConfirmUser(user)}
                          onDelete={() => {
                            setDeleteConfirmUser(user);
                            setDeleteConfirmInput('');
                          }}
                        />
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <ExpandedRow
                        user={user}
                        onRoleChange={(role) => handleRoleChange(user, role)}
                        isRolePending={changeRoleMutation.isPending}
                        canManageAdminAccess={isSuperAdmin}
                        onAdminAccessToggle={(grant) => handleAdminAccessToggle(user, grant)}
                        isAdminAccessPending={toggleAdminAccessMutation.isPending}
                      />
                    )}
                  </div>
                );
              })
            )}
            {hasNextUsersPage && (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <button
                  type="button"
                  onClick={() => fetchNextUsersPage()}
                  disabled={isFetchingNextUsersPage}
                  style={{
                    background: 'var(--tx-fill)',
                    border: 0,
                    borderRadius: 6,
                    padding: '8px 16px',
                    color: C.text,
                    fontFamily: font,
                    fontSize: 13,
                    cursor: isFetchingNextUsersPage ? 'default' : 'pointer',
                    opacity: isFetchingNextUsersPage ? 0.6 : 1,
                  }}>
                  {isFetchingNextUsersPage ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Advisor profile card overlay ──────────────────── */}
      {profileCardUser && (
        <AdvisorProfileCard user={profileCardUser} onClose={() => setProfileCardUser(null)} />
      )}

      {/* ─── Edit details dialog ────────────────────────────── */}
      {editingUser && (
        <EditDetailsDialog
          user={users.find((u) => u.userId === editingUser.userId) ?? editingUser}
          isSaving={updateProfileMutation.isPending}
          onClose={() => setEditingUser(null)}
          onSave={(fields) => {
            updateProfileMutation.mutate(
              { userId: editingUser.userId, request: fields },
              {
                onSuccess: () => {
                  showAlert(t('user.updated'), 'success');
                  setEditingUser(null);
                },
                onError: () => showAlert(t('user.update_failed'), 'error'),
              },
            );
          }}
        />
      )}

      {/* ─── Invite User dialog ─────────────────────────────── */}
      {inviteDialogOpen &&
        createPortal(
          <>
            {/* Backdrop sits below the fixed left sidebar's zIndex (1200 in
                AdminDashboardLayout.tsx) so the sidebar renders on top of it,
                untouched by the darkening — only the main content darkens. */}
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1100,
                background: 'var(--tx-backdrop)',
              }}
              onClick={() => setInviteDialogOpen(false)}
            />
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}>
            <div
              style={{
                position: 'relative',
                pointerEvents: 'auto',
                background: 'var(--tx-glass-panel)',
                backgroundImage: 'var(--tx-surface-light)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                borderRadius: '24px',
                padding: 28,
                width: 380,
                maxWidth: '90vw',
                boxShadow: 'var(--tx-shadow)',
                display: 'flex',
                flexDirection: 'column',
                gap: 18,
              }}
              onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setInviteDialogOpen(false)}
                aria-label="Close"
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--tx-muted)',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                }}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
              <span style={{ fontSize: 17, fontWeight: 600, color: C.text, fontFamily: font }}>
                Invite user
              </span>

              {inviteSuccess ? (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: C.lime,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 14px',
                    }}>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#262626"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </div>
                  <div
                    style={{
                      color: 'var(--tx-lime-ink)',
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: font,
                      marginBottom: 8,
                    }}>
                    Invite sent!
                  </div>
                  <div style={{ color: 'var(--tx-muted)', fontSize: 12, fontFamily: font }}>
                    {inviteForm.email}
                  </div>
                  <button
                    onClick={() => {
                      setInviteSuccess(false);
                      setInviteForm({ email: '', firstName: '', lastName: '', role: 'Advisor' });
                    }}
                    style={{
                      marginTop: 16,
                      background: 'var(--tx-fill-2)',
                      border: 0,
                      borderRadius: 8,
                      padding: '8px 20px',
                      color: C.text,
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: font,
                      cursor: 'pointer',
                    }}>
                    Send Another
                  </button>
                </div>
              ) : (
                <>
                  {[
                    {
                      key: 'firstName',
                      label: 'First name',
                      type: 'text',
                      placeholder: 'First name',
                    },
                    { key: 'lastName', label: 'Last name', type: 'text', placeholder: 'Last name' },
                    {
                      key: 'email',
                      label: 'Email',
                      type: 'email',
                      placeholder: 'email@example.com',
                    },
                  ].map(({ key, label, type, placeholder }) => (
                    <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--tx-muted)',
                          fontFamily: font,
                        }}>
                        {label}
                      </label>
                      <input
                        type={type}
                        placeholder={placeholder}
                        value={inviteForm[key as keyof typeof inviteForm]}
                        onChange={(e) => setInviteForm((f) => ({ ...f, [key]: e.target.value }))}
                        style={{
                          background: 'var(--tx-fill)',
                          border: 0,
                          borderRadius: 10,
                          height: 40,
                          padding: '0 12px',
                          color: 'var(--tx-muted)',
                          fontSize: 13,
                          fontFamily: font,
                          outline: 'none',
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = C.muted;
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = C.border;
                        }}
                      />
                    </div>
                  ))}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--tx-muted)',
                        fontFamily: font,
                      }}>
                      Role
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        background: 'var(--tx-fill)',
                        borderRadius: 10,
                        padding: 3,
                        gap: 3,
                      }}>
                      {(['Advisor', 'Admin'] as const).map((r) => (
                        <button
                          key={r}
                          onClick={() => setInviteForm((f) => ({ ...f, role: r }))}
                          style={{
                            flex: 1,
                            height: 34,
                            borderRadius: 7,
                            fontSize: 12,
                            fontWeight: 600,
                            fontFamily: font,
                            cursor: 'pointer',
                            border: 'none',
                            transition: 'background 0.15s, color 0.15s',
                            background: inviteForm.role === r ? C.muted : 'transparent',
                            color: inviteForm.role === r ? C.card : C.muted,
                          }}>
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>

                  {inviteError && (
                    <div
                      style={{
                        color: C.danger,
                        fontSize: 12,
                        fontFamily: font,
                        textAlign: 'center',
                      }}>
                      {inviteError}
                    </div>
                  )}

                  <button
                    disabled={
                      inviteSubmitting ||
                      !inviteForm.email ||
                      !inviteForm.firstName ||
                      !inviteForm.lastName
                    }
                    onClick={async () => {
                      setInviteSubmitting(true);
                      setInviteError(null);
                      try {
                        await createInvite(inviteForm);
                        setInviteSuccess(true);
                      } catch (err: any) {
                        setInviteError(
                          err?.response?.data?.message ??
                            'Failed to send invite. Please try again.',
                        );
                      } finally {
                        setInviteSubmitting(false);
                      }
                    }}
                    style={{
                      background: inviteSubmitting ? C.input : C.muted,
                      border: 'none',
                      borderRadius: 10,
                      height: 40,
                      color: C.card,
                      fontSize: 13,
                      fontWeight: 600,
                      letterSpacing: '-0.02em',
                      fontFamily: font,
                      cursor: inviteSubmitting ? 'wait' : 'pointer',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      opacity:
                        !inviteForm.email || !inviteForm.firstName || !inviteForm.lastName
                          ? 0.4
                          : 1,
                    }}>
                    {inviteSubmitting ? 'Sending…' : 'Send Invite'}
                    {!inviteSubmitting && (
                      <SendIcon sx={{ fontSize: 16, transform: 'rotate(-45deg)' }} />
                    )}
                  </button>
                </>
              )}
            </div>
            </div>
          </>,
          document.body,
        )}

      {/* ─── Deactivate / Activate confirm dialog ───────────────── */}
      {deactivateConfirmUser &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              background: 'var(--tx-backdrop)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => setDeactivateConfirmUser(null)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setDeactivateConfirmUser(null);
            }}>
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="deactivate-dialog-title"
              tabIndex={-1}
              style={{
                background: C.card,
                backgroundImage: 'var(--tx-surface-light)',
                borderRadius: '16px',
                padding: 28,
                width: 360,
                maxWidth: '90vw',
                boxShadow: 'var(--tx-shadow)',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
              onClick={(e) => e.stopPropagation()}>
              <span
                id="deactivate-dialog-title"
                style={{ fontSize: 15, fontWeight: 600, color: C.text, fontFamily: font }}>
                {deactivateConfirmUser.enabled ? 'Deactivate' : 'Activate'} User
              </span>
              <span style={{ fontSize: 13, color: C.muted, fontFamily: font, lineHeight: 1.5 }}>
                {deactivateConfirmUser.enabled
                  ? `${
                      deactivateConfirmUser.name ||
                      deactivateConfirmUser.email ||
                      deactivateConfirmUser.username
                    } will no longer be able to sign in.`
                  : `${
                      deactivateConfirmUser.name ||
                      deactivateConfirmUser.email ||
                      deactivateConfirmUser.username
                    } will be able to sign in again.`}
              </span>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  autoFocus
                  onClick={() => setDeactivateConfirmUser(null)}
                  style={{
                    background: 'var(--tx-fill)',
                    border: 0,
                    borderRadius: 8,
                    padding: '8px 16px',
                    color: C.muted,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: 'pointer',
                  }}>
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeactivation}
                  disabled={setUserActivationMutation.isPending}
                  style={{
                    background: C.muted,
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    color: C.card,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: setUserActivationMutation.isPending ? 'wait' : 'pointer',
                    opacity: setUserActivationMutation.isPending ? 0.6 : 1,
                  }}>
                  {deactivateConfirmUser.enabled ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* ─── Delete user confirm dialog ──────────────────────────── */}
      {deleteConfirmUser &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              background: 'var(--tx-backdrop)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => {
              setDeleteConfirmUser(null);
              setDeleteConfirmInput('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setDeleteConfirmUser(null);
                setDeleteConfirmInput('');
              }
            }}>
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-dialog-title"
              tabIndex={-1}
              style={{
                background: C.card,
                backgroundImage: 'var(--tx-surface-light)',
                borderRadius: '16px',
                padding: 28,
                width: 380,
                maxWidth: '90vw',
                boxShadow: 'var(--tx-shadow)',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
              onClick={(e) => e.stopPropagation()}>
              <span
                id="delete-dialog-title"
                style={{ fontSize: 15, fontWeight: 600, color: C.danger, fontFamily: font }}>
                Delete user
              </span>
              <span style={{ fontSize: 13, color: C.muted, fontFamily: font, lineHeight: 1.5 }}>
                This permanently deletes{' '}
                <strong style={{ color: C.text }}>
                  {deleteConfirmUser.name || deleteConfirmUser.email || deleteConfirmUser.username}
                </strong>{' '}
                and all their data. This action cannot be undone.
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label
                  htmlFor="delete-confirm-input"
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.subtle,
                    fontFamily: font,
                  }}>
                  Type{' '}
                  <strong style={{ color: C.text }}>
                    {deleteConfirmUser.email ?? deleteConfirmUser.username}
                  </strong>{' '}
                  to confirm
                </label>
                <input
                  id="delete-confirm-input"
                  autoFocus
                  type="text"
                  value={deleteConfirmInput}
                  onChange={(e) => setDeleteConfirmInput(e.target.value)}
                  placeholder={deleteConfirmUser.email ?? deleteConfirmUser.username}
                  style={{
                    background: 'var(--tx-fill)',
                    border: 0,
                    borderRadius: 8,
                    padding: '9px 12px',
                    color: C.text,
                    fontSize: 13,
                    fontFamily: font,
                    outline: 'none',
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setDeleteConfirmUser(null);
                    setDeleteConfirmInput('');
                  }}
                  style={{
                    background: 'var(--tx-fill)',
                    border: 0,
                    borderRadius: 8,
                    padding: '8px 16px',
                    color: C.muted,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: 'pointer',
                  }}>
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={
                    deleteConfirmInput !==
                      (deleteConfirmUser.email ?? deleteConfirmUser.username) ||
                    deleteUserMutation.isPending
                  }
                  style={{
                    background: 'rgba(255,77,77,0.15)',
                    border: 0,
                    borderRadius: 8,
                    padding: '8px 16px',
                    color: C.danger,
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor:
                      deleteConfirmInput !==
                        (deleteConfirmUser.email ?? deleteConfirmUser.username) ||
                      deleteUserMutation.isPending
                        ? 'not-allowed'
                        : 'pointer',
                    opacity:
                      deleteConfirmInput !==
                        (deleteConfirmUser.email ?? deleteConfirmUser.username) ||
                      deleteUserMutation.isPending
                        ? 0.4
                        : 1,
                  }}>
                  {deleteUserMutation.isPending ? 'Deleting…' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
